"use client";

import { Gift, ArrowRight } from "lucide-react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import type { PublicGiftSummary } from "@/lib/clients/LuckyGiftClient";
import { Button } from "@/components/ui/button";

interface ActiveGiftsProps {
	gifts: PublicGiftSummary[];
	totalCount: number;
	language: "zh" | "en";
}

export default function ActiveGifts({ gifts, totalCount, language }: ActiveGiftsProps) {
	const router = useRouter();

	const translations = {
		zh: {
			noGifts: "暂无红包，快来发红包吧",
			createGift: "发红包",
			moreGifts: "还有 {count} 个红包正在派发",
			viewMore: "查看更多",
			claim: "抢",
		},
		en: {
			noGifts: "No gifts yet, be the first!",
			createGift: "Create Gift",
			moreGifts: "{count} more gifts are active",
			viewMore: "View More",
			claim: "Claim",
		},
	};

	const t = translations[language];

	const handleCreateClick = () => {
		router.push("/plaza/lucky-gift?action=create");
	};

	const handleGiftClick = (giftId: string) => {
		router.push(`/plaza/lucky-gift?id=${giftId}`);
	};

	const handleViewMore = () => {
		router.push("/plaza/lucky-gift");
	};

	if (gifts.length === 0) {
		return (
			<div 
				onClick={handleCreateClick}
				className="flex flex-col items-center justify-center h-24 rounded-xl border-2 border-dashed border-light-border dark:border-dark-border bg-light-surface/50 dark:bg-dark-surface/50 cursor-pointer hover:border-red-200 dark:hover:border-red-900/40 hover:bg-red-50/20 dark:hover:bg-red-950/10 transition-all group"
			>
				<div className="w-10 h-10 rounded-full bg-light-surface dark:bg-dark-surface flex items-center justify-center mb-2 group-hover:scale-110 transition-transform shadow-sm">
					<Gift className="w-5 h-5 text-red-500/70 dark:text-red-400/70" />
				</div>
				<p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t.noGifts}</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{/* 红包列表 */}
			<div className="grid gap-2">
				{gifts.map((gift) => (
					<div
						key={gift.id}
						onClick={() => handleGiftClick(gift.id)}
						className="flex items-center justify-between p-2.5 rounded-xl bg-gradient-to-r from-[#E53935] to-[#D32F2F] dark:from-[#991B1B] dark:to-[#7F1D1D] border border-white/10 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_20px_rgba(229,57,53,0.3)] hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group relative overflow-hidden"
					>
						{/* 极简金色装饰线 - 侧边 */}
						<div className="absolute top-0 left-0 w-0.5 h-full bg-gradient-to-b from-transparent via-amber-400/60 to-transparent" />
						
						{/* 红包封口斜线装饰 - 极简感 */}
						<div className="absolute top-0 right-0 w-24 h-full bg-white/5 -skew-x-[45deg] translate-x-12 pointer-events-none" />

						{/* 悬停流光效果 */}
						<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[text-shimmer_2s_infinite] pointer-events-none" />

						<div className="flex items-center gap-3 min-w-0 relative z-10">
							{/* 代币头像 - 勋章/硬币化 */}
							<div className="relative w-9 h-9 flex-shrink-0">
								<div className="absolute inset-0 rounded-full bg-amber-400/20 animate-pulse" />
								<Image
									src={`https://images.odin.fun/token/${gift.tokenInfo.tokenId}`}
									alt={gift.tokenInfo.tokenName}
									width={36}
									height={36}
									className="rounded-full object-cover border-2 border-amber-400/50 shadow-sm relative z-10"
								/>
								<div className="absolute -bottom-1 -right-1 bg-amber-500 rounded-full p-0.5 border border-[#D32F2F] z-20 shadow-sm">
									<Gift className="w-2 h-2 text-white" />
								</div>
							</div>

							<div className="flex-1 min-w-0">
								<p className="text-sm font-black text-white dark:text-gray-100 truncate tracking-tight drop-shadow-md">
									{gift.totalAmount.toString()} <span className="text-[10px] font-medium text-white/80">{gift.tokenInfo.displayUnitName || gift.tokenInfo.tokenName}</span>
								</p>
								<p className="text-[10px] text-white/70 dark:text-gray-300/70 truncate font-medium">
									{gift.memo?.[0] || (language === "zh" ? "恭喜发财" : "Best Wishes")}
								</p>
							</div>
						</div>

						{/* 抢红包按钮 - 改造为金色圆形“封泥” */}
						<Button 
							size="sm" 
							className="h-8 w-8 p-0 bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 text-[#B71C1C] rounded-full text-sm font-black shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.2)] border border-amber-200 transition-transform active:scale-90 z-10 cursor-pointer"
						>
							{t.claim}
						</Button>
					</div>
				))}
			</div>

			{/* 更多提示 */}
			{totalCount > gifts.length && (
				<div 
					onClick={handleViewMore}
					className="flex items-center justify-center gap-1 py-1 cursor-pointer group"
				>
					<span className="text-xs text-gray-400 dark:text-gray-500 group-hover:text-red-500 transition-colors">
						{t.moreGifts.replace("{count}", (totalCount - gifts.length).toString())}
					</span>
					<ArrowRight className="w-3 h-3 text-gray-400 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all" />
				</div>
			)}
		</div>
	);
}
